import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ user = null, currentMission = null, onLogout = () => {} }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;
  const isWorkspace = location?.pathname === '/mission-workspace';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate('/mission-map-dashboard');
    } else {
      navigate('/');
    }
  };

  // Enhanced logout with proper session clearing and navigation
  const handleLogout = () => {
    // Clear all authentication tokens and user data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    
    // Call parent logout handler to clear user state
    onLogout();
    setIsUserMenuOpen(false);
    
    // Navigate to home page after clearing session
    navigate('/');
  };

  // Enhanced navigation items with more features for authenticated users
  const publicNavigationItems = [
    { label: 'Home', path: '/', icon: 'Home' },
    { label: 'Roadmaps', path: '/roadmap-selection-hub', icon: 'Map' },
    { label: 'Tutorials', path: '/tutorial-learning-center', icon: 'BookOpen' },
    { label: 'Challenges', path: '/challenges-arena', icon: 'Trophy' },
    { label: 'Interview Prep', path: '/interview-preparation-suite', icon: 'Target' }
  ];

  // Enhanced authenticated navigation with more functionalities
  const authenticatedNavigationItems = [
    { label: 'Dashboard', path: '/mission-map-dashboard', icon: 'LayoutDashboard' },
    { label: 'Workspace', path: '/mission-workspace', icon: 'Code' },
    { label: 'Profile', path: '/user-profile-dashboard', icon: 'User' },
    { label: 'Results', path: '/mission-results-summary', icon: 'BarChart3' },
    { label: 'Roadmaps', path: '/roadmap-selection-hub', icon: 'Map' },
    { label: 'Challenges', path: '/challenges-arena', icon: 'Trophy' },
    { label: 'Interview Prep', path: '/interview-preparation-suite', icon: 'Target' }
  ];

  const navigationItems = isAuthenticated ? authenticatedNavigationItems : publicNavigationItems;
  const visibleNavItems = navigationItems?.slice(0, isAuthenticated ? 7 : 5);

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 bg-card border-b border-subtle nav-height">
      <div className="flex items-center justify-between h-full px-4">
        {/* Logo Section */}
        <div className="flex items-center">
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-3 hover-scale focus-ring rounded-lg p-2 -ml-2"
          >
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} color="white" />
            </div>
            <span className="text-xl font-semibold text-foreground">
              Testing Academy
            </span>
          </button>
        </div>

        {/* Enhanced Mission Context (Workspace Only) */}
        {isWorkspace && currentMission && (
          <div className="hidden md:flex items-center space-x-4 bg-muted/50 rounded-lg px-4 py-2">
            <div className="flex items-center space-x-2">
              <Icon name="Target" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">
                {currentMission?.title}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-success transition-all duration-300"
                  style={{ width: `${currentMission?.progress || 0}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {currentMission?.progress || 0}%
              </span>
            </div>
          </div>
        )}

        {/* Enhanced Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {visibleNavItems?.map((item) => (
            <Button
              key={item?.path}
              variant={location?.pathname === item?.path ? "default" : "ghost"}
              size="sm"
              iconName={item?.icon}
              iconPosition="left"
              iconSize={16}
              onClick={() => navigate(item?.path)}
              className="text-sm"
            >
              {item?.label}
            </Button>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center space-x-2">
          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>

          {/* Enhanced User Menu or Auth Buttons */}
          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <span className="hidden sm:block text-sm font-medium">
                  {user?.username || user?.email || 'User'}
                </span>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className={`transition-transform duration-200 ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>

              {/* Enhanced User Dropdown with more features */}
              {isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-subtle rounded-lg shadow-elevation-2 animate-slide-down">
                  <div className="p-3 border-b border-subtle">
                    <p className="text-sm font-medium text-foreground">
                      {user?.username || user?.email || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Level {user?.level || 1} • {user?.levelTitle || 'Beginner Developer'}
                    </p>
                    <div className="mt-2 flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${((user?.currentXP || 0) / (user?.nextLevelXP || 1000)) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {user?.currentXP || 0} XP
                      </span>
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/user-profile-dashboard');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Dashboard</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/mission-map-dashboard');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="Map" size={16} />
                      <span>Mission Map</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/mission-results-summary');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="BarChart3" size={16} />
                      <span>Learning Analytics</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/roadmap-selection-hub');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="Map" size={16} />
                      <span>Roadmaps</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/challenges-arena');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="Trophy" size={16} />
                      <span>Challenges</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/interview-preparation-suite');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="Target" size={16} />
                      <span>Interview Prep</span>
                    </button>
                    <div className="border-t border-subtle my-2" />
                    <button
                      onClick={() => {
                        navigate('/contact');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Icon name="MessageCircle" size={16} />
                      <span>Support</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Fixed authentication buttons - proper "Sign In" button that redirects to login page
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/user-login')}
              >
                Sign In
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/user-registration')}
              >
                Start Learning Free
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Enhanced Mobile Menu with more options */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-subtle animate-slide-down">
          <div className="px-4 py-3 space-y-2">
            {/* Mission Context Mobile */}
            {isWorkspace && currentMission && (
              <div className="bg-muted/50 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">
                    {currentMission?.title}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {currentMission?.progress || 0}%
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-success transition-all duration-300"
                    style={{ width: `${currentMission?.progress || 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Navigation Items */}
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => {
                  navigate(item?.path);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                  location?.pathname === item?.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </button>
            ))}

            {/* Mobile auth section for non-authenticated users */}
            {!isAuthenticated && (
              <div className="border-t border-subtle pt-3 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mb-2"
                  onClick={() => {
                    navigate('/user-login');
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    navigate('/user-registration');
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Start Learning Free
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;